/**
 * Compact payment URL query: single `p` param (base64url JSON) so SMS/links stay short.
 * Legacy query params still read by guest app when `p` is absent.
 *
 * Payload: { o: origin, v: linkUpdatedAt ISO, e?: expiresAt ISO }
 */

import { PUBLIC_SITE_URL } from '../constants/site';

export type CompactPaymentPayload = {
  o: string;
  v?: string;
  e?: string;
};

function utf8ToBase64Url(json: string): string {
  const bytes = new TextEncoder().encode(json);
  let bin = "";
  bytes.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function decodeCompactPaymentPayload(
  pParam: string | null | undefined,
): CompactPaymentPayload | null {
  if (!pParam || !String(pParam).trim()) return null;
  try {
    let b64 = String(pParam).trim().replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const json = atob(b64);
    const obj = JSON.parse(json) as CompactPaymentPayload;
    if (!obj || typeof obj.o !== "string" || (obj.v !== undefined && typeof obj.v !== "string")) return null;
    return obj;
  } catch {
    return null;
  }
}

export function encodeCompactPaymentQuery(
  origin: string,
  _linkUpdatedAt?: string,
  _expiresAt?: string | null,
): string {
  const payload: CompactPaymentPayload = {
    o: origin,
  };
  if (_linkUpdatedAt) payload.v = _linkUpdatedAt;
  if (_expiresAt) payload.e = _expiresAt;
  const base64 = utf8ToBase64Url(JSON.stringify(payload));
  return `p=${encodeURIComponent(base64)}`;
}

export const PAYMENT_LINK_BASE = `${PUBLIC_SITE_URL}/home/payment`;

export function buildPaymentPageUrl(
  bookingId: string,
  origin: string,
  _linkUpdatedAt?: string,
  _expiresAt?: string | null,
): string {
  const q = encodeCompactPaymentQuery(origin, _linkUpdatedAt, _expiresAt);
  return `${PAYMENT_LINK_BASE}/${bookingId}?${q}`;
}

export function parsePaymentLinkSearch(search: string): {
  origin: string | null;
  linkUpdatedAt: string | null;
  expiresAt: string | null;
} {
  const qs = search.startsWith("?") ? search.slice(1) : search;
  const params = new URLSearchParams(qs);
  const compact = params.get("p");
  const decoded = compact ? decodeCompactPaymentPayload(compact) : null;
  if (decoded) {
    return {
      origin: decoded.o,
      linkUpdatedAt: decoded.v ?? null,
      expiresAt: decoded.e ?? null,
    };
  }
  return {
    origin: params.get("origin"),
    linkUpdatedAt: params.get("linkUpdatedAt"),
    expiresAt: params.get("expiresAt"),
  };
}
